/**
 * MCP 营销落地页共享组件（/ai/chatgpt、/ai/claude、/ai/codex 复用）。
 * 注意事项：
 * 1. 纯展示型 SSR 落地页主体；站点 header/footer 由 layout 注入。
 * 2. 交互（流程切换 / FAQ 折叠 / 提问复制 / 图片放大）为 React state + CSS，初始渲染保持 SSR/CSR 一致。
 * 3. 图标使用站点全局 alicdn iconfont（layout 注入 //at.alicdn.com/t/c/font_1265799_*.css），MIcon 把 name 作为字形类输出。
 * 4. 各平台差异只在传入的 data（HERO/PROMPTS/CAPABILITIES/OPERATION/FAQ/CTA）与图片资源；本组件不含平台关键字。
 * 5. 操作步骤每一步支持「截图」或「代码块」两种形态：step.image → 可点击放大的图；step.code → 终端/代码块。
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import style from './index.less';

function joinClassNames() {
  return Array.prototype.slice.call(arguments).filter(Boolean).join(' ');
}

/**
 * 平滑滚动到操作步骤区（#operation），供所有 Start Using 按钮共用。
 * 不用 scrollIntoView / scrollTo({behavior:'smooth'})：站点脚本会间歇性给 html 设
 * inline overflow:hidden，浏览器原生平滑滚动会被静默拦截（瞬时 scrollTo(0,y) 不受影响）。
 * 因此用 rAF 自驱动画逐帧瞬时滚动，桌面/移动端行为一致，也无老浏览器兼容问题。
 */
function scrollToOperation() {
  if (typeof document === 'undefined') return;
  var target = document.getElementById('operation');
  if (!target) return;
  var getY = function () {
    return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
  };
  var start = getY();
  var dist = target.getBoundingClientRect().top - 72; // 72px 给站点顶栏留空间
  if (!dist) return;
  var DURATION = 600;
  var t0 = null;
  var step = function (ts) {
    if (t0 === null) t0 = ts;
    var p = Math.min((ts - t0) / DURATION, 1);
    // easeInOutQuad
    var eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
    window.scrollTo(0, start + dist * eased);
    if (p < 1) window.requestAnimationFrame(step);
  };
  if (window.requestAnimationFrame) {
    window.requestAnimationFrame(step);
  } else {
    window.scrollTo(0, start + dist);
  }
}

function MIcon({ name, className }) {
  return <span className={joinClassNames('iconfont', name, className)}></span>;
}

/* 已复制反馈图标（内联 SVG check，stroke 继承 currentColor）。
   默认复制图标用站点 iconfont 字形 iconfuzhijianyue（见 RecommendedPromptsSection）。 */
function CheckIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/* 关闭图标（放大查看遮罩的关闭按钮），内联 SVG。 */
function CloseIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

/* 推荐提问 chip 的类目图标（Lucide 风格内联 SVG，stroke 继承 currentColor）。
   constants.js 中每条 prompt 的 icon 字段 → 此处路径。 */
const PROMPT_ICON_PATHS = {
  search: <><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></>,
  tag: (
    <>
      <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
      <circle cx="7.5" cy="7.5" r="0.5" fill="currentColor" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
      <path d="M2 12h20" />
    </>
  ),
  truck: (
    <>
      <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
      <path d="M15 18H9" />
      <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
      <circle cx="17" cy="18" r="2" />
      <circle cx="7" cy="18" r="2" />
    </>
  ),
  order: (
    <>
      <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="M12 11h4" />
      <path d="M12 16h4" />
      <path d="M8 11h.01" />
      <path d="M8 16h.01" />
    </>
  ),
  card: (
    <>
      <rect width="20" height="14" x="2" y="5" rx="2" />
      <line x1="2" x2="22" y1="10" y2="10" />
    </>
  ),
  wallet: (
    <>
      <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 1-1 1v-2a1 1 0 0 0-1-1" />
      <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" />
    </>
  ),
  store: (
    <>
      <path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7" />
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4" />
      <path d="M2 7h20" />
    </>
  ),
  gauge: (
    <>
      <path d="m12 14 4-4" />
      <path d="M3.34 19a10 10 0 1 1 17.32 0" />
    </>
  ),
  alert: (
    <>
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </>
  ),
  terminal: (
    <>
      <path d="m4 17 6-6-6-6" />
      <path d="M12 19h8" />
    </>
  ),
  key: (
    <>
      <path d="m21 2-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </>
  ),
  plug: (
    <>
      <path d="M12 22v-5" />
      <path d="M9 8V2" />
      <path d="M15 8V2" />
      <path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z" />
    </>
  ),
};

function PromptIcon({ name, className }) {
  var paths = PROMPT_ICON_PATHS[name];
  if (!paths) return null;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      {paths}
    </svg>
  );
}

/* 复制文本到剪贴板：优先 navigator.clipboard，降级到隐藏 textarea + execCommand，
   兼容非安全上下文/老浏览器。 */
function copyText(text) {
  if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).catch(function () {
      fallbackCopyText(text);
    });
    return;
  }
  fallbackCopyText(text);
}

function fallbackCopyText(text) {
  if (typeof document === 'undefined') return;
  var ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.top = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
  } catch (e) {
    /* 复制失败静默忽略，UI 仍给出乐观反馈 */
  }
  document.body.removeChild(ta);
}

/* Codex 头部插图（来源：myproject/changename/codex插图.html，内联以保留 CSS 动画）。
   一个人在电脑前使用 CJ Dropshipping MCP 的扁平矢量插画；.line/.cursor/.steam 动画由 index.less 作用域内全局类驱动。 */
function CodexHeroIllustration() {
  return (
    <svg viewBox="0 0 860 560" role="img" aria-label="A person sitting at a computer using CJ Dropshipping's MCP feature">
      <defs>
        <linearGradient id="cjxWall" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff3e9" />
          <stop offset="1" stopColor="#ffe2cb" />
        </linearGradient>
        <linearGradient id="cjxScreen" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#23252e" />
          <stop offset="1" stopColor="#16181f" />
        </linearGradient>
        <linearGradient id="cjxBrandG" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ff8a33" />
          <stop offset="1" stopColor="#e56a00" />
        </linearGradient>
        <radialGradient id="cjxGlow" cx="50%" cy="40%" r="60%">
          <stop offset="0" stopColor="#ff7700" stopOpacity="0.25" />
          <stop offset="1" stopColor="#ff7700" stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect width="860" height="560" fill="url(#cjxWall)" />
      <circle cx="430" cy="220" r="260" fill="url(#cjxGlow)" />

      <g opacity="0.85">
        <rect x="92" y="70" width="86" height="64" rx="6" fill="#fff" stroke="#f1c9a5" strokeWidth="3" />
        <path d="M100 122 l22 -28 16 16 14 -20 18 32 z" fill="#ffd2a6" />
        <circle cx="158" cy="88" r="7" fill="#ff7700" />
      </g>
      <g>
        <rect x="708" y="92" width="44" height="40" rx="6" fill="#e9d4bf" />
        <path d="M730 92 c-18 -26 -42 -20 -40 2 c20 -6 30 6 40 -2z" fill="#67b06b" />
        <path d="M730 92 c18 -26 42 -20 40 2 c-20 -6 -30 6 -40 -2z" fill="#7cc47f" />
        <path d="M730 96 v-22" stroke="#5b9b5f" strokeWidth="3" />
      </g>

      <rect x="0" y="440" width="860" height="120" fill="#ffdcbf" />
      <line x1="0" y1="440" x2="860" y2="440" stroke="#f6c79c" strokeWidth="3" />

      <rect x="120" y="430" width="620" height="16" rx="6" fill="#caa179" />
      <rect x="150" y="446" width="14" height="78" rx="4" fill="#b88a5f" />
      <rect x="696" y="446" width="14" height="78" rx="4" fill="#b88a5f" />

      <g>
        <rect x="300" y="150" width="300" height="206" rx="14" fill="#0f1117" />
        <rect x="312" y="162" width="276" height="182" rx="8" fill="url(#cjxScreen)" />
        <circle cx="330" cy="180" r="4.5" fill="#ff5f57" />
        <circle cx="346" cy="180" r="4.5" fill="#febc2e" />
        <circle cx="362" cy="180" r="4.5" fill="#28c840" />
        <g transform="translate(442,172)">
          <rect x="0" y="0" width="134" height="20" rx="10" fill="url(#cjxBrandG)" />
          <text x="67" y="14" textAnchor="middle" fontFamily="ui-monospace, Menlo, monospace" fontSize="12" fontWeight="700" fill="#fff" letterSpacing="0.5">⬡ CJ · MCP</text>
        </g>
        <g fontFamily="ui-monospace, 'SF Mono', Menlo, monospace" fontSize="11">
          <g className="line"><text x="328" y="216" fill="#7fc7ff">🧑 You:</text><text x="372" y="216" fill="#e6e9ef">Order these phone cases for me</text></g>
          <g className="line"><text x="328" y="236" fill="#5ec27a">⬡ CJ Dropshipping MCP · connected</text></g>
          <g className="line"><text x="328" y="256" fill="#ff8a33">⚙ search_products</text><text x="432" y="256" fill="#9aa0aa">("phone case")</text></g>
          <g className="line"><text x="328" y="276" fill="#ff8a33">⚙ create_order</text><text x="416" y="276" fill="#9aa0aa"> → #CJ8821</text></g>
          <g className="line"><text x="328" y="300" fill="#5ec27a">✓ track_shipment · SF1234…</text><rect className="cursor" x="468" y="291" width="7" height="12" fill="#ff7700" /></g>
        </g>
      </g>
      <rect x="440" y="356" width="20" height="40" fill="#bdbfc7" />
      <rect x="408" y="396" width="84" height="12" rx="6" fill="#aeb0b8" />

      <g>
        <rect x="372" y="412" width="180" height="26" rx="6" fill="#e7e2db" transform="skewX(-12)" style={{ transformOrigin: '462px 425px' }} />
        <g fill="#cfc8bf" transform="skewX(-12)" style={{ transformOrigin: '462px 425px' }}>
          <rect x="382" y="418" width="14" height="6" rx="2" /><rect x="402" y="418" width="14" height="6" rx="2" />
          <rect x="422" y="418" width="14" height="6" rx="2" /><rect x="442" y="418" width="14" height="6" rx="2" />
          <rect x="462" y="418" width="14" height="6" rx="2" /><rect x="482" y="418" width="14" height="6" rx="2" />
          <rect x="412" y="428" width="60" height="6" rx="3" />
        </g>
      </g>

      <g>
        <path className="steam" d="M648 372 q6 -8 0 -16" stroke="#d9b08c" strokeWidth="3" fill="none" strokeLinecap="round" />
        <path className="steam b" d="M660 372 q6 -8 0 -16" stroke="#d9b08c" strokeWidth="3" fill="none" strokeLinecap="round" />
        <rect x="636" y="384" width="40" height="34" rx="6" fill="#fff" stroke="#e7c39d" strokeWidth="2" />
        <path d="M676 392 h10 a8 8 0 0 1 0 16 h-10" fill="none" stroke="#e7c39d" strokeWidth="3" />
        <rect x="640" y="388" width="32" height="8" rx="3" fill="#ff7700" />
      </g>

      <g>
        <rect x="150" y="300" width="40" height="150" rx="16" fill="#ff9a4d" />
        <rect x="158" y="312" width="24" height="120" rx="12" fill="#ffb072" />
        <rect x="206" y="470" width="12" height="40" fill="#9aa0aa" />
        <path d="M178 524 l44 -20 44 20" stroke="#9aa0aa" strokeWidth="10" fill="none" strokeLinecap="round" />
        <path d="M188 446 q4 -120 78 -120 q70 0 70 96 l0 24 q-74 18 -148 0 z" fill="#ff7700" />
        <path d="M196 360 q70 -20 132 6" stroke="#ffae6b" strokeWidth="4" fill="none" opacity="0.6" />
        <path d="M300 376 q70 8 96 44" stroke="#e56a00" strokeWidth="26" fill="none" strokeLinecap="round" />
        <circle cx="398" cy="424" r="13" fill="#ffd9b8" />
        <rect x="248" y="300" width="26" height="30" rx="10" fill="#ffd9b8" />
        <circle cx="262" cy="282" r="34" fill="#ffd9b8" />
        <path d="M228 280 q-6 -44 38 -48 q44 -2 40 40 q-10 -18 -34 -18 q-30 0 -44 26z" fill="#3a2f2a" />
        <path d="M228 282 q-4 18 6 30 q-16 -4 -14 -24z" fill="#3a2f2a" />
        <circle cx="232" cy="288" r="6" fill="#f6c5a0" />
      </g>

      <g opacity="0.6">
        <g transform="translate(632,196)" stroke="#ff7700" strokeWidth="2.5" fill="none">
          <circle cx="0" cy="0" r="4" fill="#ff7700" />
          <circle cx="26" cy="-12" r="4" fill="#ffb072" />
          <circle cx="26" cy="14" r="4" fill="#ffb072" />
          <path d="M4 -2 L22 -11 M4 2 L22 12" />
        </g>
        <g transform="translate(112,232)">
          <path d="M0 8 L20 0 L40 8 L40 30 L20 38 L0 30 Z" fill="#ffd2a6" stroke="#e56a00" strokeWidth="2" />
          <path d="M0 8 L20 16 L40 8 M20 16 L20 38" stroke="#e56a00" strokeWidth="2" fill="none" />
          <path d="M14 4 L34 12" stroke="#ff7700" strokeWidth="3" />
        </g>
        <text x="700" y="306" fontSize="18" fill="#ffb072" fontFamily="monospace" fontWeight="700">⬡</text>
      </g>
    </svg>
  );
}

/* Claude 头部插图（来源：myproject/changename/claude-cj-mcp插图.html，内联以保留 CSS 动画）。
   一个人轻松地用 Claude + CJ Dropshipping MCP 处理店铺数据；.seq/.bar/.spark/.steam/.bolt 动画由 index.less 作用域内全局类驱动。 */
function ClaudeHeroIllustration() {
  return (
    <svg viewBox="0 0 900 600" role="img" aria-label="A person efficiently handling their store data with CJ Dropshipping's MCP inside Claude">
      <defs>
        <linearGradient id="clxWall" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fdf2e8" />
          <stop offset="1" stopColor="#f6e2d0" />
        </linearGradient>
        <linearGradient id="clxScreen" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fffaf6" />
          <stop offset="1" stopColor="#fdf1e8" />
        </linearGradient>
        <linearGradient id="clxClaudeG" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#e58a68" />
          <stop offset="1" stopColor="#c2603f" />
        </linearGradient>
        <linearGradient id="clxCjG" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ff9a3d" />
          <stop offset="1" stopColor="#ff7700" />
        </linearGradient>
        <linearGradient id="clxChart" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#5ec27a" />
          <stop offset="1" stopColor="#3fa564" />
        </linearGradient>
        <radialGradient id="clxGlow" cx="62%" cy="36%" r="55%">
          <stop offset="0" stopColor="#d97757" stopOpacity="0.22" />
          <stop offset="1" stopColor="#d97757" stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect width="900" height="600" fill="url(#clxWall)" />
      <circle cx="560" cy="230" r="290" fill="url(#clxGlow)" />

      <rect x="0" y="470" width="900" height="130" fill="#f0d6bd" />
      <line x1="0" y1="470" x2="900" y2="470" stroke="#e7c19f" strokeWidth="3" />
      <rect x="120" y="458" width="700" height="16" rx="6" fill="#caa179" />

      <g>
        <rect x="300" y="120" width="430" height="288" rx="16" fill="#0f1117" />
        <rect x="312" y="132" width="406" height="264" rx="10" fill="url(#clxScreen)" />
        <circle cx="332" cy="152" r="4.5" fill="#ff5f57" />
        <circle cx="348" cy="152" r="4.5" fill="#febc2e" />
        <circle cx="364" cy="152" r="4.5" fill="#28c840" />
        <g transform="translate(392,146)">
          <g fill="#d97757">
            <g className="bolt">
              <rect x="6" y="-1" width="4" height="16" rx="2" />
              <rect x="6" y="-1" width="4" height="16" rx="2" transform="rotate(45 8 7)" />
              <rect x="6" y="-1" width="4" height="16" rx="2" transform="rotate(90 8 7)" />
              <rect x="6" y="-1" width="4" height="16" rx="2" transform="rotate(135 8 7)" />
            </g>
          </g>
          <text x="26" y="12" fontFamily="ui-sans-serif, system-ui, sans-serif" fontSize="13" fontWeight="700" fill="#3a2f2a">Claude</text>
        </g>
        <g transform="translate(596,138)">
          <rect x="0" y="0" width="106" height="20" rx="10" fill="url(#clxCjG)" />
          <circle cx="13" cy="10" r="3" fill="#fff" />
          <text x="60" y="14" textAnchor="middle" fontFamily="ui-monospace, Menlo, monospace" fontSize="10" fontWeight="700" fill="#fff">CJ · MCP ✓</text>
        </g>
        <line x1="324" y1="168" x2="706" y2="168" stroke="#f0ddcb" strokeWidth="1.5" />

        <g fontFamily="ui-sans-serif, system-ui, sans-serif">
          <g className="seq">
            <rect x="328" y="182" width="168" height="34" rx="9" fill="#f3e3d6" />
            <text x="340" y="197" fontSize="10.5" fill="#5b4a3e">Sort out today's store data</text>
            <text x="340" y="210" fontSize="10.5" fill="#5b4a3e">and reorder the bestsellers.</text>
          </g>
          <g className="seq" fontFamily="ui-monospace, Menlo, monospace">
            <text x="330" y="240" fontSize="9.5" fill="#d97757">⤷ via CJ Dropshipping MCP</text>
          </g>
          <g className="seq" fontFamily="ui-monospace, Menlo, monospace" fontSize="9.5">
            <text x="330" y="262" fill="#ff7700">⚙ check_inventory</text><text x="438" y="262" fill="#9aa0aa">· 312 SKUs</text>
            <text x="330" y="280" fill="#ff7700">⚙ create_order</text><text x="430" y="280" fill="#9aa0aa">· 48 items</text>
            <text x="330" y="298" fill="#ff7700">⚙ track_shipment</text><text x="446" y="298" fill="#9aa0aa">· 27 parcels</text>
          </g>
          <g className="seq">
            <text x="330" y="322" fontFamily="ui-monospace, Menlo, monospace" fontSize="9.5" fill="#3fa564">✓ Done in 4s — 128 orders synced</text>
          </g>
        </g>

        <g transform="translate(516,182)">
          <rect x="0" y="0" width="186" height="200" rx="8" fill="#fff" stroke="#f0ddcb" strokeWidth="1.5" />
          <text x="12" y="20" fontFamily="ui-sans-serif, system-ui" fontSize="10" fontWeight="700" fill="#7a6555">Store Overview</text>
          <g fontFamily="ui-sans-serif, system-ui">
            <rect x="12" y="30" width="78" height="40" rx="6" fill="#fff5ee" />
            <text x="22" y="46" fontSize="9" fill="#9a7d6a">Orders</text>
            <text x="22" y="62" fontSize="14" fontWeight="700" fill="#ff7700">1,284 ↑</text>
            <rect x="96" y="30" width="78" height="40" rx="6" fill="#eefaf1" />
            <text x="106" y="46" fontSize="9" fill="#9a7d6a">Revenue</text>
            <text x="106" y="62" fontSize="14" fontWeight="700" fill="#3fa564">+23%</text>
          </g>
          <g transform="translate(12,86)">
            <line x1="0" y1="86" x2="162" y2="86" stroke="#ece0d4" strokeWidth="1.5" />
            <rect className="bar" x="6" y="50" width="16" height="36" rx="3" fill="url(#clxChart)" />
            <rect className="bar" x="32" y="40" width="16" height="46" rx="3" fill="url(#clxChart)" />
            <rect className="bar" x="58" y="56" width="16" height="30" rx="3" fill="url(#clxChart)" />
            <rect className="bar" x="84" y="30" width="16" height="56" rx="3" fill="url(#clxChart)" />
            <rect className="bar" x="110" y="20" width="16" height="66" rx="3" fill="url(#clxChart)" />
            <rect className="bar" x="136" y="8" width="16" height="78" rx="3" fill="url(#clxCjG)" />
            <path d="M8 60 L40 50 L66 62 L92 40 L118 30 L150 14" fill="none" stroke="#d97757" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="150" cy="14" r="3.5" fill="#d97757" />
          </g>
        </g>
      </g>
      <rect x="505" y="408" width="20" height="42" fill="#bdbfc7" />
      <rect x="472" y="450" width="86" height="12" rx="6" fill="#aeb0b8" />

      <rect x="360" y="438" width="150" height="22" rx="5" fill="#e7e2db" transform="skewX(-10)" style={{ transformOrigin: '435px 449px' }} />

      <g>
        <rect x="150" y="298" width="44" height="160" rx="16" fill="#d97757" />
        <rect x="158" y="312" width="28" height="128" rx="13" fill="#e89070" />
        <rect x="208" y="468" width="12" height="44" fill="#9aa0aa" />
        <path d="M178 524 l44 -22 44 22" stroke="#9aa0aa" strokeWidth="10" fill="none" strokeLinecap="round" />
        <path d="M186 458 q0 -116 80 -116 q66 0 66 96 l0 20 q-72 16 -146 0 z" fill="url(#clxClaudeG)" />
        <path d="M246 366 q-30 -34 6 -56" fill="none" stroke="#c2603f" strokeWidth="22" strokeLinecap="round" />
        <path d="M286 366 q30 -34 -6 -56" fill="none" stroke="#c2603f" strokeWidth="22" strokeLinecap="round" />
        <rect x="252" y="300" width="26" height="28" rx="10" fill="#ffd9b8" />
        <circle cx="265" cy="286" r="33" fill="#ffd9b8" />
        <path d="M232 286 q-6 -42 36 -46 q42 -2 38 38 q-10 -16 -32 -16 q-28 0 -42 24z" fill="#3a2f2a" />
        <circle cx="236" cy="292" r="6" fill="#f6c5a0" />
        <circle cx="252" cy="262" r="9" fill="#ffd9b8" />
        <circle cx="280" cy="262" r="9" fill="#ffd9b8" />
        <path d="M256 296 q9 7 18 0" fill="none" stroke="#b07a52" strokeWidth="2.5" strokeLinecap="round" />
      </g>

      <g transform="translate(96,150)">
        <rect x="0" y="0" width="92" height="30" rx="15" fill="#fff" stroke="#ff7700" strokeWidth="2" />
        <text x="46" y="20" textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontSize="13" fontWeight="800" fill="#ff7700">10x faster</text>
      </g>

      <g fill="#d97757">
        <path className="spark" d="M740 180 l4 10 l10 4 l-10 4 l-4 10 l-4 -10 l-10 -4 l10 -4 z" />
        <path className="spark b" d="M120 300 l3 8 l8 3 l-8 3 l-3 8 l-3 -8 l-8 -3 l8 -3 z" fill="#ff7700" />
        <path className="spark c" d="M770 320 l3 7 l7 3 l-7 3 l-3 7 l-3 -7 l-7 -3 l7 -3 z" />
      </g>
      <g transform="translate(770,236)" opacity="0.9">
        <path d="M0 8 L18 0 L36 8 L36 28 L18 36 L0 28 Z" fill="#ffd2a6" stroke="#e56a00" strokeWidth="2" />
        <path d="M0 8 L18 16 L36 8 M18 16 L18 36" stroke="#e56a00" strokeWidth="2" fill="none" />
      </g>

      <g>
        <path className="steam" d="M636 444 q6 -8 0 -16" stroke="#d9b08c" strokeWidth="3" fill="none" strokeLinecap="round" />
        <path className="steam b" d="M648 444 q6 -8 0 -16" stroke="#d9b08c" strokeWidth="3" fill="none" strokeLinecap="round" />
        <rect x="624" y="448" width="36" height="20" rx="5" fill="#fff" stroke="#e7c39d" strokeWidth="2" />
        <path d="M660 452 h8 a7 7 0 0 1 0 14 h-8" fill="none" stroke="#e7c39d" strokeWidth="3" />
      </g>
    </svg>
  );
}

/* hero.illustration 字段 → 头部插图组件（split 布局用） */
const HERO_ILLUSTRATIONS = {
  codexPerson: CodexHeroIllustration,
  claudePerson: ClaudeHeroIllustration,
};

function HeroTextBlock({ hero }) {
  // Start Using Now → 平滑滚动到操作步骤区（#operation），逻辑见 scrollToOperation。
  const handleStartUsing = useCallback(scrollToOperation, []);
  return (
    <>
      <div className={style.heroBadge}>
        <MIcon name="iconstar2" />
        <span className={style.labelSm}>{hero.badge}</span>
      </div>
      <h1 className={style.heroTitle}>
        {hero.title}
        <span>{hero.titleHighlight}</span>
      </h1>
      <p className={joinClassNames(style.bodyLg, style.heroSubtitle)}>{hero.subtitle}</p>
      <div className={style.heroButtons}>
        <button
          type="button"
          className={joinClassNames(style.btnPrimary, style.labelMd)}
          onClick={handleStartUsing}
        >
          {hero.primaryCta}
        </button>
        <a
          className={joinClassNames(style.btnSecondary, style.labelMd)}
          href={hero.secondaryCtaHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          {hero.secondaryCta}
        </a>
      </div>
    </>
  );
}

function HeroSection({ hero }) {
  // Start Using Now → 平滑滚动到操作步骤区（#operation），逻辑见 scrollToOperation。
  const handleStartUsing = useCallback(scrollToOperation, []);

  // split 布局：左插图 / 右文字（移动端纵向堆叠），用于 Codex 等无聊天预览的页面。
  if (hero.layout === 'split') {
    var Illustration = HERO_ILLUSTRATIONS[hero.illustration] || null;
    return (
      <section className={style.hero}>
        <div className={style.heroInner}>
          <div className={style.heroSplit}>
            {Illustration ? (
              <div className={style.heroSplitMedia}>
                <Illustration />
              </div>
            ) : null}
            <div className={style.heroSplitText}>
              <HeroTextBlock hero={hero} />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={style.hero}>
      <div className={style.heroInner}>
        <div className={style.heroBadge}>
          <MIcon name="iconstar2" />
          <span className={style.labelSm}>{hero.badge}</span>
        </div>
        <h1 className={style.heroTitle}>
          {hero.title}
          <span>{hero.titleHighlight}</span>
        </h1>
        <p className={joinClassNames(style.bodyLg, style.heroSubtitle)}>{hero.subtitle}</p>
        <div className={style.heroButtons}>
          <button
            type="button"
            className={joinClassNames(style.btnPrimary, style.labelMd)}
            onClick={handleStartUsing}
          >
            {hero.primaryCta}
          </button>
          <a
            className={joinClassNames(style.btnSecondary, style.labelMd)}
            href={hero.secondaryCtaHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            {hero.secondaryCta}
          </a>
        </div>

        <div className={style.heroPreviewWrap}>
          <div className={joinClassNames(style.glassCard, style.heroPreviewCard)}>
            <div className={style.heroPreviewInner}>
              <div className={style.previewBar}>
                <div className={style.previewDots}>
                  <div className={joinClassNames(style.previewDot, style.dotRed)} />
                  <div className={joinClassNames(style.previewDot, style.dotYellow)} />
                  <div className={joinClassNames(style.previewDot, style.dotGreen)} />
                </div>
                <div className={joinClassNames(style.previewBarTitle, style.labelSm)}>{hero.previewTitle}</div>
                <div className={style.previewBarSpacer} />
              </div>
              <div className={style.previewBody}>
                <div className={style.chatRow}>
                  <div className={joinClassNames(style.chatAvatar, style.avatarUser)}>
                    <MIcon name="icondenglu" />
                  </div>
                  <div className={joinClassNames(style.userBubble, style.bodyMd)}>{hero.userMessage}</div>
                </div>
                <div className={style.chatRow}>
                  <div className={joinClassNames(style.chatAvatar, style.avatarBot)}>
                    <MIcon name="iconquanlianlu" />
                  </div>
                  <div className={joinClassNames(style.botBubble, style.bodyMd)}>
                    <p>{hero.botIntro}</p>
                    <div className={style.botProductGrid}>
                      {hero.botProducts.map(function (product) {
                        return (
                          <div key={product.name} className={style.botProductCard}>
                            <img
                              className={style.botProductThumb}
                              src={product.image}
                              alt={product.name}
                            />
                            <div>
                              <div className={style.botProductName}>{product.name}</div>
                              <div className={style.botProductPrice}>{product.price}</div>
                              {product.ship ? (
                                <div className={style.botProductShip}>{product.ship}</div>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={joinClassNames(style.heroBlob, style.heroBlob1)} />
          <div className={joinClassNames(style.heroBlob, style.heroBlob2)} />
        </div>
      </div>
    </section>
  );
}

function RecommendedPromptsSection({ prompts }) {
  // 记录最近被复制的条目索引，短暂展示「Copied!」反馈后归位。
  const [copiedIndex, setCopiedIndex] = useState(-1);
  const timerRef = useRef(null);

  const handleCopy = useCallback(function (text, index) {
    copyText(text);
    setCopiedIndex(index);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(function () {
      setCopiedIndex(-1);
    }, 1600);
  }, []);

  return (
    <section className={style.prompts} id="prompts">
      <div className={style.container}>
        <div className={style.sectionHeadCenter}>
          <h2 className={style.headlineLg}>{prompts.title}</h2>
          <p className={joinClassNames(style.bodyMd, style.narrow)}>{prompts.subtitle}</p>
        </div>
        <div className={style.promptGrid}>
          {prompts.items.map(function (item, index) {
            var isCopied = index === copiedIndex;
            return (
              <button
                key={item.text}
                type="button"
                className={joinClassNames(
                  style.glassCard,
                  style.promptCard,
                  isCopied ? style.promptCardCopied : ''
                )}
                onClick={function () {
                  handleCopy(item.text, index);
                }}
                title={prompts.copyLabel}
                aria-label={prompts.copyLabel + ': ' + item.text}
              >
                <PromptIcon name={item.icon} className={style.promptLeadIcon} />
                <span className={joinClassNames(style.bodyMd, style.promptText)}>{item.text}</span>
                <span className={style.promptCopy}>
                  {isCopied ? (
                    <>
                      <CheckIcon className={style.promptCopyIcon} />
                      <span className={style.promptCopyLabel}>{prompts.copiedLabel}</span>
                    </>
                  ) : (
                    <MIcon name="iconfuzhijianyue" className={style.promptCopyGlyph} />
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CapabilitiesSection({ capabilities }) {
  return (
    <section className={style.capabilities} id="capabilities">
      <div className={style.container}>
        <div className={style.capHead}>
          <h2 className={style.headlineXl}>
            {capabilities.title}
            <br />
            <span>{capabilities.titleHighlight}</span>
          </h2>
          <p className={style.bodyLg}>{capabilities.subtitle}</p>
        </div>
        <div className={style.capGrid}>
          {capabilities.items.map(function (item) {
            return (
              <div key={item.title} className={style.capCard}>
                <div className={style.capImageWrap}>
                  <img src={item.image} alt={item.title} />
                </div>
                <h3 className={style.headlineMd}>{item.title}</h3>
                <p className={style.bodyMd}>{item.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function OperationSection({ operation }) {
  const [activeStep, setActiveStep] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const current = operation.steps[activeStep];

  const selectStep = useCallback(function (index) {
    setActiveStep(index);
    setZoomed(false); // 切步时收起放大态（代码块步骤无图，不可放大）
  }, []);

  const closeZoom = useCallback(function () {
    setZoomed(false);
  }, []);

  // 放大查看时：锁定页面滚动 + 支持 Esc 关闭。
  useEffect(
    function () {
      if (!zoomed || typeof document === 'undefined') return undefined;
      var prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      var onKey = function (e) {
        if (e.key === 'Escape' || e.keyCode === 27) setZoomed(false);
      };
      document.addEventListener('keydown', onKey);
      return function () {
        document.body.style.overflow = prevOverflow;
        document.removeEventListener('keydown', onKey);
      };
    },
    [zoomed]
  );

  return (
    <section className={style.operation} id="operation">
      <div className={style.container}>
        <div className={joinClassNames(style.sectionHeadCenter)}>
          <h2 className={style.headlineLg}>{operation.title}</h2>
          <p className={joinClassNames(style.bodyMd, style.narrow)}>{operation.subtitle}</p>
        </div>
        <div className={style.operationLayout}>
          <div className={style.operationNav}>
            {operation.steps.map(function (step, index) {
              var isActive = index === activeStep;
              return (
                <button
                  key={step.title}
                  type="button"
                  className={joinClassNames(style.stepBtn, isActive ? style.stepBtnActive : '')}
                  onClick={function () {
                    selectStep(index);
                  }}
                >
                  <div className={style.stepRow}>
                    <span className={style.stepNum}>{index + 1}</span>
                    <div>
                      <div className={style.stepTitle}>{step.title}</div>
                      <div className={style.stepLabel}>{step.label}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          {/* 移动端横向步骤条：已完成绿色 / 当前橙色高亮 / 未完成灰色，桌面端隐藏 */}
          <div className={style.operationStepper}>
            <div className={style.stepperTrack}>
              {operation.steps.map(function (step, index) {
                var isActive = index === activeStep;
                var isDone = index < activeStep;
                return (
                  <React.Fragment key={step.title}>
                    {index > 0 ? (
                      <span
                        className={joinClassNames(
                          style.stepperLine,
                          index <= activeStep ? style.stepperLineDone : ''
                        )}
                      />
                    ) : null}
                    <button
                      type="button"
                      aria-label={step.title}
                      aria-current={isActive ? 'step' : undefined}
                      className={joinClassNames(
                        style.stepperDot,
                        isDone ? style.stepperDotDone : '',
                        isActive ? style.stepperDotActive : ''
                      )}
                      onClick={function () {
                        selectStep(index);
                      }}
                    >
                      {index + 1}
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
            <div className={style.stepperCurrent}>
              <div className={style.stepTitle}>{current.title}</div>
              <div className={style.stepLabel}>{current.label}</div>
            </div>
          </div>
          <div className={joinClassNames(style.glassCard, style.operationContent)}>
            <div key={activeStep} className={style.operationFade}>
              {current.image ? (
                <img
                  className={style.operationImg}
                  src={current.image}
                  alt={current.alt}
                  onClick={function () {
                    setZoomed(true);
                  }}
                  role="button"
                  aria-label="Click to enlarge"
                />
              ) : (
                <pre className={style.operationCode}>
                  <code>{current.code}</code>
                </pre>
              )}
              <div className={style.operationDescWrap}>
                {/* desc 可能含 <a> 等 HTML 标签（文案源自本地常量，非用户输入），用 dangerouslySetInnerHTML 渲染 */}
                <p
                  className={joinClassNames(style.bodyLg, style.operationDesc)}
                  dangerouslySetInnerHTML={{ __html: current.desc }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* 点击放大：全屏遮罩 + 居中大图，点击遮罩 / 关闭按钮 / Esc 关闭。仅截图步骤可放大。 */}
      {zoomed && current.image ? (
        <div className={style.lightbox} onClick={closeZoom} role="dialog" aria-modal="true">
          <button
            type="button"
            className={style.lightboxClose}
            onClick={closeZoom}
            aria-label="Close"
          >
            <CloseIcon className={style.lightboxCloseIcon} />
          </button>
          <img
            className={style.lightboxImg}
            src={current.image}
            alt={current.alt}
            onClick={function (e) {
              e.stopPropagation();
            }}
          />
        </div>
      ) : null}
    </section>
  );
}

function FaqSection({ faq }) {
  const [openFaqs, setOpenFaqs] = useState([0]);

  const toggleFaq = useCallback(function (index) {
    setOpenFaqs(function (prev) {
      if (prev.indexOf(index) > -1) {
        return prev.filter(function (item) {
          return item !== index;
        });
      }
      return [index];
    });
  }, []);

  return (
    <section className={style.faq}>
      <div className={style.faqInner}>
        <div className={style.sectionHeadCenter}>
          <h2 className={style.headlineLg}>{faq.title}</h2>
          <p className={style.bodyMd}>{faq.subtitle}</p>
        </div>
        <div className={style.faqList}>
          {faq.items.map(function (item, index) {
            var isOpen = openFaqs.indexOf(index) > -1;
            return (
              <div key={item.question} className={style.faqItem}>
                <button
                  type="button"
                  className={style.faqQuestion}
                  onClick={function () {
                    toggleFaq(index);
                  }}
                >
                  <span>{item.question}</span>
                  <MIcon name={isOpen ? 'icon123' : 'iconbianzu21'} className={joinClassNames(style.faqIcon, isOpen ? style.faqIconOpen : '')} />
                </button>
                <div className={joinClassNames(style.faqAnswer, style.bodyMd, isOpen ? style.faqAnswerOpen : '')}>
                  {item.answer}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CtaSection({ cta }) {
  return (
    <section className={style.cta}>
      <div className={joinClassNames(style.glassCard, style.ctaCard)}>
        <div className={style.ctaBg} />
        <div className={style.ctaInner}>
          <h2 className={style.headlineXl}>{cta.title}</h2>
          <p className={joinClassNames(style.bodyLg, style.ctaSubtitle)}>{cta.subtitle}</p>
          <a
            className={joinClassNames(style.ctaButton, style.labelMd)}
            href={cta.docHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            {cta.docButton}
          </a>
        </div>
      </div>
    </section>
  );
}

/**
 * @param {Object} props.data - 平台数据：{ HERO, PROMPTS, CAPABILITIES, OPERATION, FAQ, CTA }
 * @param {string} props.moduleName - data-gtag-module 标识（chatgpt / claude / codex）
 */
function McpLanding({ data, moduleName }) {
  return (
    <div className={style.chatgptPage} data-gtag-module={moduleName}>
      <HeroSection hero={data.HERO} />
      <CapabilitiesSection capabilities={data.CAPABILITIES} />
      <RecommendedPromptsSection prompts={data.PROMPTS} />
      <OperationSection operation={data.OPERATION} />
      <FaqSection faq={data.FAQ} />
      <CtaSection cta={data.CTA} />
    </div>
  );
}

export default McpLanding;
